/** @jsx jsx */
import { jsx, Box, Card, Text } from 'theme-ui'
import ProductLink from '../Link'
import { GatsbyImage } from 'gatsby-plugin-image'
import QuickProductAddToCartButton from '~/components/Product/AddToCartButton/Quick'
import formatMoney from '~/utils/formatMoney'
import { useIntl } from 'gatsby-plugin-intl'
import ImgLoader from '~/components/Generic/ImgLoader'
import useVariantAvailability from '~/hooks/components/use-variant-availability'
import { Fragment, useState } from 'react'

const BikeQuickAddCard = ({ collectionName, lineup, product, variant }) => {
  const { locale } = useIntl()
  const [apiError, setApiError] = useState(null)
  const { externalProductPageUrl, customCtaText } = product
  const {
    isFetchingAvailability,
    isAvailable,
    error: availabilityError
  } = useVariantAvailability({
    variantId: variant.shopifyId
  })

  return (
    <Card
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 0
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'block',
          marginBottom: ['8px', '14px', '14px', '19px', '19px'],
          ':hover .visibleOnHover': {
            opacity: [null, 1]
          }
        }}
      >
        <ProductLink
          product={product}
          variant={variant}
          lineup={lineup}
          collectionName={collectionName}
          from="product image"
          externalLink={externalProductPageUrl}
          ariaLabel={`${product.name} page from product photo`}
          sx={{
            'div:first-of-type': {
              transition: '0.25s ease'
            },
            '&:hover div:first-of-type': {
              visibility: 'hidden',
              opacity: 0
            },
            ':hover div:last-of-type': {
              visibility: 'visible',
              opacity: 1
            }
          }}
        >
          <ImgLoader
            image={variant.mainImage}
            alt={variant.mainImage.title || ''}
            imgStyle={{ objectFit: 'contain', objectPosition: 'center' }}
          />

          {variant.images && variant.images[0] && (
            <GatsbyImage
              image={variant.images[0].gatsbyImageData}
              alt={variant.images[0].description ?? variant.images[0].title}
              sx={{
                position: 'absolute !important',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                visibility: 'hidden',
                transition: '0.25s ease'
              }}
              imgStyle={{ objectFit: 'contain', objectPosition: 'center' }}
            />
          )}
        </ProductLink>

        <QuickProductAddToCartButton
          setApiError={setApiError}
          limitOnePerCustomer={variant.limitOnePerCustomer}
          price={variant.price}
          quantity={1}
          regularPrice={variant.regularPrice}
          sku={variant.sku}
          variantId={variant.shopifyId}
          isFetchingAvailability={isFetchingAvailability}
          isAvailable={isAvailable}
          availabilityError={availabilityError}
          comingSoon={variant.comingSoon}
          externalLink={externalProductPageUrl}
          customCtaText={customCtaText}
        />
      </Box>

      <ProductLink
        product={product}
        variant={variant}
        lineup={lineup}
        collectionName={collectionName}
        externalLink={externalProductPageUrl}
        from="price text"
        sx={{ fontSize: ['1.1rem', 2, null, 5], textAlign: 'center' }}
      >
        {variant.name}
      </ProductLink>

      {!externalProductPageUrl && (
        <Text
          sx={{
            fontSize: ['1.1rem', 2, null, 5],
            fontWeight: 'light',
            mt: 'auto'
          }}
        >
          {variant.regularPrice !== variant.price ? (
            <Fragment>
              <span sx={{ textDecoration: 'line-through', mr: '10px' }}>
                {formatMoney(variant.regularPrice, { locale })}
              </span>
              <span sx={{ color: 'red' }}>
                {formatMoney(variant.price, { locale })}
              </span>
            </Fragment>
          ) : (
            formatMoney(variant.price, { locale })
          )}
        </Text>
      )}
      {apiError && (
        <Text
          color="errorDark"
          variant="textLink"
          mt="1.5rem"
          sx={{ textAlign: 'center' }}
        >
          {apiError.toString()}
        </Text>
      )}
    </Card>
  )
}

export default BikeQuickAddCard
